import { use_slice_generation } from '@/hooks/store/use_slices';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { DTO__image_crop_box } from '@shared/dto/+image/image_asset_base/DTO__image_crop_box';
import { DTO__image_size } from '@shared/dto/+image/image_asset_base/DTO__image_size';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { ENUM__invocation_type } from '@shared/dto/generation/graph_invoke/_invocations/_base/ENUM__invocation_type';
import { INVOCATION_IMAGE__viewport } from '@shared/dto/generation/graph_invoke/_invocations/image/INVOCATION_IMAGE__viewport';
import { useMemo } from 'react';

interface PROPS {
  field: FIELD__image_asset;
  container_size: DTO__image_size;
}

export function G_IMAGE_OVERLAY__crop_box({ field, container_size }: PROPS) {
  /*
   * STORE
   */
  const { generation_current } = use_slice_generation(['generation_current']);
  let { graph } = generation_current.tool;

  /*
   * STATE
   */
  const natural_size = useMemo(() => get_image_size(), [field]);
  const image_size_scaled = useMemo(() => DTO__image_size.scale_to_container(natural_size, container_size), [natural_size, container_size]);

  const crop_box_scaled = useMemo(() => get_crop_box_scaled(), [image_size_scaled, field, generation_current]);

  /*
   * EFFECTS
   */

  const overlay_areas = useMemo(() => {
    if (crop_box_scaled.is_stub()) return [];

    const { x, y, width, height } = crop_box_scaled;

    const right = x + width;
    const bottom = y + height;

    return [
      { top: 0, left: 0, width: '100%', height: y },
      { top: bottom, left: 0, width: '100%', height: `calc(100% - ${bottom}px)` },
      { top: y, left: 0, width: x, height },
      { top: y, left: right, width: `calc(100% - ${right}px)`, height },
    ];
  }, [crop_box_scaled]);

  /*
   * METHODS
   */
  function get_image_size() {
    if (!field.image__is_initialized) return DTO__image_size.ZERO.clone();

    return field.asset.image_size.clone();
  }

  function get_crop_box_scaled() {
    if (field.meta.node_type != ENUM__invocation_type.IMAGE__VIEWPORT) return DTO__image_crop_box.CREATE_STUB();
    if (!graph.nodes_map.has(field.meta.node_id)) return DTO__image_crop_box.CREATE_STUB(); //nodes not exists

    let node = graph.node__get(field.meta.node_id);
    assert_instanceof(node, INVOCATION_IMAGE__viewport);

    let field_crop_box = node.crop_box;
    let crop_box = field_crop_box.crop_box;
    let { asset: image_asset } = field;

    if (image_asset.is_stub()) return DTO__image_crop_box.ZERO.clone();
    if (image_size_scaled.is_zero()) return DTO__image_crop_box.ZERO.clone();

    let out = DTO__image_crop_box.original_to_scaled(crop_box, image_size_scaled, image_asset.image_size);

    return out;
  }

  /*
   * HTML
   */

  if (field.meta.kind__is_output) return null;
  if (field.meta.node_type != ENUM__invocation_type.IMAGE__VIEWPORT) return null; //no need to show cropbox
  if (crop_box_scaled.is_stub()) return null; //not initialized yet

  return (
    <>
      {overlay_areas.map((style, i) => (
        <div key={i} className="info__crop_box_overlay absolute bg-black/80 pointer-events-none" {...{ style }} />
      ))}

      <div
        className="info__crop_box_crop_box_border absolute border-2 border-dashed border-component-border pointer-events-none"
        style={crop_box_scaled.to_css_object()}
      />
    </>
  );
}
